/**
 * The item vocabulary shared by the API, the web app and the enrichment
 * prompts. Display labels live in the UI message catalogue, not here.
 */
export const ITEM_STATUSES = ["draft", "in_stock", "listed", "sold", "shipped", "donated", "lost", "damaged"] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];

/** Gone for good with no sale; the purchase price becomes sunk cost. */
export const WRITE_OFF_REASONS = ["damaged", "donated", "lost"] as const;
export type WriteOffReason = (typeof WRITE_OFF_REASONS)[number];

/** Statuses a person can set directly; sold and shipped come from recording a sale. */
export const SETTABLE_STATUSES = ["draft", "in_stock", "listed"] as const;

export const CONDITION_GRADES = ["nwt", "nwot", "excellent", "great", "good", "fair"] as const;
export type ConditionGrade = (typeof CONDITION_GRADES)[number];

export const AUDIENCES = ["adult", "kids", "baby", "unisex_all"] as const;
export const SEASONS = ["spring", "summer", "fall", "winter", "all_season"] as const;
export const LISTING_STATUSES = ["queued", "active", "sold", "delisted", "ended", "error"] as const;
export const COST_KINDS = [
  "shipping_label", "packaging", "platform_fee", "payment_processing",
  "mileage", "laundering", "repair", "promotion", "other",
] as const;
export const PACKAGING = ["poly", "bubble", "other", "none"] as const;

export const PHOTO_KINDS = ["item", "brand_tag", "price_tag", "flaw", "listing"] as const;
export type PhotoKind = (typeof PHOTO_KINDS)[number];

/**
 * Guided capture slots. Each maps to the coarse kind the vision step reads
 * (price and brand tags drive the price and brand reads). Any number of
 * `extra` photos may follow.
 */
export const PHOTO_SLOTS = [
  { slot: "front", kind: "item", required: true },
  { slot: "back", kind: "item", required: false },
  { slot: "brand_tag", kind: "brand_tag", required: false },
  { slot: "price_tag", kind: "price_tag", required: false },
  { slot: "detail", kind: "listing", required: false },
  { slot: "measurement", kind: "listing", required: false },
  { slot: "flaw", kind: "flaw", required: false },
] as const satisfies readonly { slot: string; kind: PhotoKind; required: boolean }[];
export const EXTRA_SLOT = "extra";
export const SLOT_KEYS = [...PHOTO_SLOTS.map((s) => s.slot), EXTRA_SLOT] as const;
export type SlotKey = (typeof SLOT_KEYS)[number];

export function kindOfSlot(slot: SlotKey): PhotoKind {
  return PHOTO_SLOTS.find((s) => s.slot === slot)?.kind ?? "listing";
}

/**
 * Whether a new photo of this kind starts private: kept for the seller, never
 * posted and never the cover. A thrift-store price tag does; the seller can
 * move any photo either way afterwards.
 */
export function privateByDefault(kind: PhotoKind): boolean {
  return kind === "price_tag";
}

/** Photo formats the web app uploads after resizing (ARCHITECTURE.md §3.2). */
export const PHOTO_CONTENT_TYPES = ["image/webp", "image/jpeg"] as const;
export const PHOTO_MAX_BYTES = 8 * 1024 * 1024;
